import { useEffect, useMemo, useState } from "react";
import TodayWorkout from "../components/TodayWorkout";
import GuidedWorkout from "../components/GuidedWorkout";
import ExerciseLogger from "../components/ExerciseLogger";
import WorkoutHistory from "../components/WorkoutHistory";
import StreakDisplay from "../components/StreakDisplay";
import { api } from "../utils/api";
import { colors, spacing, radius } from "@forge/ui";

type Exercise = {
  id: string;
  name: string;
  sets?: number;
  reps?: number;
  rest?: number;
};

type Plan = {
  id: string;
  name: string;
  date?: string;
  exercises: Exercise[];
};

type ExerciseProgress = {
  completed?: boolean;
  setsCompleted?: number;
  reps?: number;
  weight?: number;
  notes?: string;
};

type ProgressPayload = {
  planId: string;
  date: string;
  exercises: Array<{
    id: string;
    setsCompleted?: number;
    reps?: number;
    weight?: number;
    notes?: string;
    completed?: boolean;
  }>;
};

type HistoryEntry = {
  date: string;
  planName: string;
  status: string;
};

// Reusable components moved to separate files: TodayWorkout, ExerciseLogger, WorkoutHistory, StreakDisplay

export default function WorkoutsPage() {
  const [todayPlan, setTodayPlan] = useState<Plan | null>(null);
  const [progress, setProgress] = useState<Record<string, ExerciseProgress>>(
    {},
  );
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guided, setGuided] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const plan = await api.get<Plan | null>("/api/user/plans/workout");
        setTodayPlan(plan);
        const hist = await api.get<HistoryEntry[]>("/api/user/progress");
        setHistory(hist);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function updateExercise(exId: string, data: ExerciseProgress) {
    setProgress((p) => ({ ...p, [exId]: { ...(p[exId] || {}), ...data } }));
  }

  function startWorkout() {
    if (todayPlan && todayPlan.exercises.length > 0) setGuided(true);
  }

  function onGuidedComplete(
    results: Record<string, { setsCompleted: number; reps?: number; weight?: number }>,
  ) {
    setProgress((p) => {
      const next = { ...p };
      for (const [exId, data] of Object.entries(results)) {
        next[exId] = { ...(next[exId] || {}), ...data, completed: true };
      }
      return next;
    });
    setGuided(false);
  }

  async function completeWorkout() {
    if (!todayPlan) return;
    const payload: ProgressPayload = {
      planId: todayPlan.id,
      date: new Date().toISOString(),
      exercises: todayPlan.exercises.map((ex) => ({
        id: ex.id,
        ...progress[ex.id],
      })),
    };
    try {
      await api.post<unknown>("/api/user/progress", payload);
      // refresh history after logging
      const hist = await api.get<HistoryEntry[]>("/api/user/progress");
      setHistory(hist);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to log workout");
    }
  }

  const emptyState = useMemo(() => !todayPlan, [todayPlan]);

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <div style={styles.leftCol}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : emptyState ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyTitle}>No workout scheduled today</div>
              <div style={styles.emptySubtitle}>
                Stay motivated—plan tomorrow, and hit your goals!
              </div>
            </div>
          ) : guided && todayPlan ? (
            <GuidedWorkout
              exercises={todayPlan.exercises}
              onComplete={onGuidedComplete}
            />
          ) : (
            <TodayWorkout
              plan={todayPlan!}
              progress={progress}
              onUpdate={updateExercise}
            />
          )}
          {!guided && (
            <>
              <div style={styles.section}>
                {todayPlan?.exercises.map((ex) => (
                  <ExerciseLogger
                    key={ex.id}
                    exercise={ex}
                    value={progress[ex.id] || {}}
                    onChange={(v) => updateExercise(ex.id, v)}
                  />
                ))}
              </div>
              <div style={styles.actions}>
                <button onClick={startWorkout} style={styles.startBtn}>
                  Start Workout
                </button>
                <button onClick={completeWorkout} style={styles.completeBtn}>
                  Complete Workout
                </button>
              </div>
            </>
          )}
          {error && <div style={styles.error}>{error}</div>}
        </div>
        <div style={styles.rightCol}>
          <StreakDisplay history={history.map((h) => ({ date: h.date }))} />
          <WorkoutHistory items={history} />
        </div>
      </div>
    </div>
  );
}

const styles: { [k: string]: React.CSSProperties | { [k: string]: React.CSSProperties } } = {
  container: {
    padding: spacing.lg,
    fontFamily: "Inter, system-ui, Arial",
    backgroundColor: colors.bg,
    minHeight: "100vh",
    color: colors.text,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 320px",
    gap: spacing.lg,
  },
  leftCol: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.md,
  },
  rightCol: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.md,
  },
  section: {
    padding: spacing.sm,
  },
  card: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: radius.md,
    padding: spacing.md,
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing.sm,
  },
  date: { fontSize: 12, color: colors.textMuted },
  exercisesList: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.sm,
  },
  exerciseRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
  },
  checkbox: {
    width: 20,
    height: 20,
  },
  exerciseName: {
    fontSize: 14,
  },
  loading: {
    padding: 20,
  },
  emptyState: {
    padding: 20,
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 700,
  },
  emptySubtitle: {
    color: colors.textDim,
    marginTop: 6,
  },
  startBtn: {
    padding: `${spacing.md}px ${spacing.lg}px`,
    marginRight: spacing.sm,
    borderRadius: radius.md,
    border: "none",
    backgroundColor: colors.accent,
    color: colors.text,
    cursor: "pointer",
  },
  completeBtn: {
    padding: `${spacing.md}px ${spacing.lg}px`,
    borderRadius: radius.md,
    border: "none",
    backgroundColor: colors.success,
    color: colors.text,
    cursor: "pointer",
  },
  error: { color: colors.danger },
  logger: { backgroundColor: colors.surfaceElevated, padding: spacing.md, borderRadius: radius.md },
  loggerRow: { display: "flex", alignItems: "center", gap: spacing.sm, marginBottom: 6 },
  label: { width: 90, fontSize: 12, color: colors.textMuted },
  input: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bg,
    color: colors.text,
  },
  saveBtn: {
    marginTop: 6,
    padding: `${spacing.sm}px ${spacing.md}px`,
    borderRadius: 6,
    border: "none",
    backgroundColor: colors.accent,
    color: colors.text,
    cursor: "pointer",
  },
  historyRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: `1px solid ${colors.border}`,
  },
  historyPlan: { marginLeft: spacing.sm, color: colors.textMuted },
  historyStatus: { color: colors.success },
  rightColTitle: { fontSize: 14, fontWeight: 700 },
  streakValue: {
    fontSize: 28,
    fontWeight: 700,
    textAlign: "center",
    padding: spacing.md,
  },
};
